import { ChevronDown, ChevronUp, Thermometer, Wind, Sunrise, Gauge, Droplets } from 'lucide-react';

import { HuntRatingBar } from '@/components/common/HuntRatingBar';
import { DataCockpit } from '@/components/common/DataCockpit';
import { ActivityTrendGraph } from '@/components/forecast/ActivityTrendGraph';
import type { DataCockpitCell } from '@/types/data-cockpit';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ForecastDayRowData {
  /** Unix timestamp for the day */
  dt: number;
  /** Label — e.g. "TUE 14" */
  label: string;
  /** Hunt score 0–100 */
  score: number;
  /** Temperature high °F */
  tempMax: number;
  /** Temperature low °F */
  tempMin: number;
  /** Wind speed mph */
  windSpeed: number;
  /** Wind direction abbreviation e.g. "NW" */
  windDir: string;
  /** Moon phase emoji */
  moonEmoji: string;
  /** Pressure inHg */
  pressureInHg: number;
  /** Pressure trend arrow */
  pressureTrend: string;
  /** Civil dawn time formatted */
  civilDawnTime: string;
  /** Civil dusk time formatted */
  civilDuskTime: string;
  /** Whether a cold front is detected */
  isColdFront: boolean;
  /** Precipitation probability 0–100 */
  precipitationProbability?: number;
  /** Precipitation volume in inches */
  precipitationVolume?: number;
}

interface ForecastDayRowProps {
  data: ForecastDayRowData;
  /** Whether this row is currently expanded (parent controls accordion state) */
  isExpanded: boolean;
  /** Called when the user clicks the header to toggle expand/collapse */
  onToggle: () => void;
  /**
   * Optional row index — when provided, generates per-index data-testid attributes:
   * `forecast-day-{index}`, `forecast-day-header-{index}`, `forecast-day-expanded-{index}`.
   * When omitted, uses generic `forecast-row` / `forecast-row-expanded` attributes.
   */
  index?: number;
  /**
   * Optional hourly activity scores for the ActivityTrendGraph sparkline.
   * Only rendered when length >= 2.
   */
  activityScores?: number[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRECIP_FORECAST_THRESHOLD = 0;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ForecastDayRow({
  data,
  isExpanded,
  onToggle,
  index,
  activityScores,
}: ForecastDayRowProps) {
  const {
    label,
    score,
    tempMax,
    tempMin,
    windSpeed,
    windDir,
    moonEmoji,
    pressureInHg,
    pressureTrend,
    civilDawnTime,
    civilDuskTime,
    isColdFront,
    precipitationProbability,
    precipitationVolume,
  } = data;

  const qualityLabel =
    score >= 80 ? 'Prime' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Poor';

  const hasIndex = index !== undefined;
  const rowTestId = hasIndex ? `forecast-day-${index}` : 'forecast-row';
  const headerTestId = hasIndex ? `forecast-day-header-${index}` : undefined;
  const expandedTestId = hasIndex ? `forecast-day-expanded-${index}` : 'forecast-row-expanded';

  // Precipitation badge for the expanded thermal cell — sits at the bottom of the badge slot
  // (below all other cell content; matches dashboard ConditionsWidget pattern)
  const precipExpandedContent =
    precipitationProbability !== undefined ? (
      <div
        style={{ display: 'flex', flexDirection: 'row', gap: 4, marginTop: 2 }}
        data-testid="forecast-precip-expanded"
      >
        <span
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: '#6e6a63',
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          PRECIP
        </span>
        <span
          style={{ fontSize: 12, color: '#6e6a63', fontFamily: 'DM Sans, sans-serif' }}
        >
          {precipitationProbability}%
          {precipitationVolume !== undefined && precipitationVolume > 0
            ? ` · ${precipitationVolume.toFixed(1)} in`
            : null}
        </span>
      </div>
    ) : undefined;

  const cockpitCells: DataCockpitCell[] = [
    {
      icon: <Thermometer size={12} />,
      primary: `${Math.round(tempMax)}°`,
      secondary: `Lo ${Math.round(tempMin)}°`,
      label: 'TEMP',
      testId: hasIndex ? `forecast-score-bar-${index}` : 'forecast-cockpit-temp',
      badge: precipExpandedContent,
    },
    {
      icon: <Wind size={12} />,
      primary: `${Math.round(windSpeed)} mph`,
      secondary: windDir,
      label: 'WIND',
      testId: 'forecast-cockpit-wind',
    },
    {
      icon: <Sunrise size={12} />,
      primary: civilDawnTime,
      secondary: civilDuskTime,
      label: 'HUNT WINDOW',
      testId: 'forecast-cockpit-hunt-window',
    },
    {
      icon: <Gauge size={12} />,
      primary: `${pressureInHg.toFixed(2)}`,
      secondary: `${pressureTrend} trend`,
      label: 'PRESSURE',
      testId: 'forecast-cockpit-pressure',
    },
  ];

  return (
    <div
      className="border-b border-[var(--color-border)] last:border-b-0"
      data-testid={rowTestId}
    >
      {/* Collapsed header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left py-3 px-4 flex items-center gap-3 hover:bg-[var(--color-elevated)] transition-colors"
        aria-expanded={isExpanded}
        data-testid={headerTestId}
      >
        {/* Day label — DM Sans bold, "TUE 14" format */}
        <div className="w-14 flex-shrink-0">
          <p className="font-bold text-[var(--color-text-primary)] text-body-sm leading-tight">
            {label}
          </p>
        </div>

        {/* Hunt rating bar */}
        <div className="flex-shrink-0">
          <HuntRatingBar score={score} />
        </div>

        {/* Hi/lo temp + precipitation probability (inline) */}
        <div className="flex-1 min-w-0 text-body-xs text-[var(--color-text-muted)]">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>{Math.round(tempMax)}° / {Math.round(tempMin)}°</span>
            {precipitationProbability !== undefined && precipitationProbability > PRECIP_FORECAST_THRESHOLD && (
              <span
                style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 10, color: '#9a9590' }}
                data-testid="forecast-precip-probability"
              >
                <Droplets size={12} style={{ color: '#9a9590' }} aria-hidden="true" />
                {precipitationProbability}%
              </span>
            )}
          </div>
        </div>

        {/* Wind speed */}
        <div className="flex-shrink-0 text-body-xs text-[var(--color-text-muted)]">
          {windDir} {Math.round(windSpeed)}
        </div>

        {/* Moon icon */}
        <div className="flex-shrink-0 text-base" aria-hidden="true">
          {moonEmoji}
        </div>

        {/* Expand chevron */}
        <div className="flex-shrink-0 text-[var(--color-text-muted)]">
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </button>

      {/* Expanded section */}
      {isExpanded && (
        <div
          className="bg-[var(--color-elevated)] pb-3 flex flex-col gap-2"
          data-testid={expandedTestId}
        >
          {/* Quality label + cold front flag */}
          <div className="flex items-center gap-2 px-4 pt-3">
            <span className="text-body-sm font-semibold text-[var(--color-text-primary)]">
              {qualityLabel}
            </span>
            {isColdFront && (
              <span
                className="text-body-xs font-semibold text-blue-400 border border-blue-400/30 bg-blue-400/10 rounded px-1.5 py-0.5"
                data-testid={hasIndex ? `cold-front-flag-${index}` : 'forecast-row-cold-front'}
              >
                Cold Front
              </span>
            )}
          </div>

          {/* DataCockpit 2×2 grid */}
          <DataCockpit cells={cockpitCells} />

          {/* Activity trend sparkline — rendered when activityScores provided and length >= 2 */}
          {activityScores !== undefined && activityScores.length >= 2 && (
            <div className="px-4 pt-1">
              <ActivityTrendGraph scores={activityScores} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
